import {
  Alert,
  AlertTitle,
  Box,
  Button,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { backend, ImportFieldValuesResponse } from "Backend";
import { ChangeEvent, FC, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { useSnackbar } from "notistack";
import { selectFormFields } from "redux/slices/form.slice";
import triggerDownload from "helpers/triggerDownload";
import { P } from "theme/typography";
import { FormField } from "generated/graphql";
import truncateString from "helpers/truncateString";
import ProfileImportErrorTable from "./profileimport/ProfileImportErrorTable";
import { LoadingButton } from "@mui/lab";
import RequestStatus from "redux/slices/RequestStatus";
import { FormFieldSelect } from "components/FormFieldSelect";

interface DeleteSuccessesProps {
  response: ImportFieldValuesResponse;
}
const DeleteSuccesses: FC<DeleteSuccessesProps> = ({ response }) => {
  return (
    <TableContainer sx={{ maxHeight: 440 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>Case ID</TableCell>
            <TableCell>Group Index</TableCell>
            <TableCell>Deleting</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(response.records || []).map((record, index) => (
            <TableRow key={index}>
              <TableCell>{record.clientCaseId}</TableCell>
              <TableCell>{record.valueGroupIndex}</TableCell>
              <TableCell>Deleting</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface ImportSuccessesProps {
  response: ImportFieldValuesResponse;
  field: FormField;
}

const ImportSuccesses: FC<ImportSuccessesProps> = ({ response, field }) => {
  return (
    <TableContainer sx={{ maxHeight: 440 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>Case ID</TableCell>
            {field?.cells.map((cell) => <TableCell key={cell.id}>{cell.name}</TableCell>)}
            <TableCell>Notes</TableCell>
            <TableCell>Existing Group Index</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(response.records || []).map((record, index) => (
            <TableRow key={index}>
              <TableCell>{record.clientCaseId}</TableCell>
              {record.cellValues.map((cellValue, index) => {
                return <TableCell key={index}>{cellValue.value?.toString() || ""}</TableCell>;
              })}
              <TableCell title={record.notes}>{truncateString(record.notes || "", 50)}</TableCell>
              <TableCell>{record.valueGroupIndex}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export function ImportToProfileDialog({ formId, onClose }: { formId: number; onClose: () => void }) {
  const profileFields = useAppSelector(selectFormFields);
  const [selectedProfileFieldId, setSelectedProfileFieldId] = useState<number | undefined>(
    profileFields[0]?.id || undefined
  );
  const selectedField = profileFields.find((f) => f.id === selectedProfileFieldId);
  const [file, setFile] = useState<File | undefined>();
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const [importResponse, setImportResponse] = useState<ImportFieldValuesResponse | undefined>();
  const [deleteText, setDeleteText] = useState<string>("");
  const { enqueueSnackbar } = useSnackbar();

  const onFile = (evt: ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0];
    evt.target.value = "";

    if (file && selectedField) {
      setImportResponse(undefined);
      setFile(file);
      backend.importFieldValuesFromExcel(file, formId, selectedField.id, true).then((response) => {
        if (!response.records && !response.errors) {
          enqueueSnackbar("No Records Found", { variant: "error" });
          setFile(undefined);
        } else {
          setImportResponse(response);
        }
      });
    }
  };

  const importToProfile = () => {
    if (!selectedField) return;
    setStatus(RequestStatus.PENDING);
    backend
      .importFieldValuesFromExcel(file!, formId, selectedField.id, false)
      .then((res) => {
        if (res.records?.length) enqueueSnackbar(`${res.recordTotal} Records Imported`, { variant: "success" });
        else enqueueSnackbar("No Records Imported", { variant: "error" });
      })
      .catch((error) => {
        if (error.response?.status === 504) {
          enqueueSnackbar(
            "We ran out of time for a standard API call but the import will continue in the background.",
            {
              variant: "info",
            }
          );
          setStatus(RequestStatus.SUCCEEDED);
        } else {
          enqueueSnackbar(
            error.response?.data?.message ? error.response.data.message : "There was a problem with the upload.",
            { variant: "error" }
          );
          setStatus(RequestStatus.FAILED);
        }
      })
      .finally(() => {
        onClose();
      });
  };

  const downloadTemplate = async (fieldId: number) => {
    const { data, fileName } = await backend.downloadProfileTemplate(fieldId);
    triggerDownload(
      new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName
    );
  };

  const isImportFailure = (importResponse && importResponse.errors.length > 0) || false;
  const isImportSuccess = (importResponse && importResponse.records.length > 0) || false;
  const isDeleteSuccess = (importResponse && importResponse.records.reduce((a, b) => a || b.delete, false)) || false;

  return (
    <Box>
      <Box sx={{ my: 2 }}>
        {!importResponse && (
          <P variant="sm">
            To import profile data, first select your profile question and download the template file. Then, fill in
            your profile responses and upload the file below.
          </P>
        )}
        {importResponse && isImportSuccess && selectedField !== undefined && !isDeleteSuccess && (
          <>
            <Alert severity="info">
              <AlertTitle>Check Succeeded</AlertTitle>
              Ready to import <strong>{importResponse.recordTotal}</strong> responses across{" "}
              <strong>{importResponse.recordCases}</strong> cases
            </Alert>
            <ImportSuccesses response={importResponse} field={selectedField} />
          </>
        )}
        {importResponse && isImportSuccess && selectedField !== undefined && isDeleteSuccess && (
          <>
            <Alert severity="info">
              <AlertTitle>Check Succeeded</AlertTitle>
              Ready to delete <strong>{importResponse.recordTotal}</strong> responses across{" "}
              <strong>{importResponse.recordCases}</strong> cases
            </Alert>
            <DeleteSuccesses response={importResponse} />
          </>
        )}

        {profileFields.length > 0 && !importResponse && (
          <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mt: 2 }}>
            <FormFieldSelect
              fieldId={selectedProfileFieldId}
              changeFieldId={(fieldId) => setSelectedProfileFieldId(fieldId)}
            />
            <Button
              onClick={() =>
                selectedProfileFieldId !== undefined ? downloadTemplate(selectedProfileFieldId) : undefined
              }
              disabled={selectedProfileFieldId === undefined}>
              Download Template
            </Button>
          </Box>
        )}
      </Box>
      {(importResponse === undefined || isImportFailure) && (
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mt: 2, gap: 1 }}>
          {file && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <P variant="smBold">Selected File:</P>
              <P variant="sm">{file.name}</P>
            </Box>
          )}
          <Button
            disabled={selectedProfileFieldId === undefined}
            size="small"
            id="import_choose_file"
            component="label"
            color="default"
            variant="outlined">
            {file ? "Change File Selection" : "Choose Excel File"}
            <input
              type="file"
              autoComplete="off"
              tabIndex={-1}
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: "none" }}
              onChange={onFile}
            />
          </Button>
        </Box>
      )}
      {importResponse && isImportFailure && (
        <Box sx={{ mt: 1 }}>
          <Alert severity="error">
            We found {importResponse.errorTotal}
            {importResponse.errorTotal === 1 ? " error " : " errors "}
            in the file. Please fix the errors and try again.
          </Alert>
          <ProfileImportErrorTable errors={importResponse.errors} />
        </Box>
      )}
      <DialogActions>
        {isDeleteSuccess && (
          <TextField
            placeholder={"Type 'delete' to delete"}
            onChange={(v) => {
              setDeleteText(v.target.value);
            }}
          />
        )}

        <Button
          onClick={() => {
            onClose();
          }}
          sx={{ marginRight: 1 }}>
          Cancel
        </Button>
        <LoadingButton
          loading={status === RequestStatus.PENDING}
          disabled={!isImportSuccess || (isDeleteSuccess && "delete" !== deleteText)}
          variant="contained"
          onClick={importToProfile}>
          {isDeleteSuccess ? "Delete" : "Import"}
        </LoadingButton>
      </DialogActions>
    </Box>
  );
}
